/**
 * M28d — Shared app shell with mobile drawer.
 *
 * Renders a sticky header with:
 *   - Brand link (left)
 *   - Primary nav (desktop, hidden < md)
 *   - ThemeToggle + auth actions (right)
 *   - Hamburger → full-height overlay drawer (mobile only)
 *
 * Pages opt in by wrapping content in <AppShell>…</AppShell>. Pages that
 * render their own custom chrome (login, oauth-callback) stay as-is.
 */
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/lib/auth";

interface NavItem {
  label: string;
  to: string;
  requiresAuth: boolean;
}

const NAV: NavItem[] = [
  { label: "Catalog", to: "/catalog", requiresAuth: false },
  { label: "Favorites", to: "/favorites", requiresAuth: true },
  { label: "Jobs", to: "/jobs", requiresAuth: true },
  { label: "Wallet", to: "/wallet", requiresAuth: true },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Auto-close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [drawerOpen]);

  const visibleNav = NAV.filter((n) => !n.requiresAuth || user);

  return (
    <div className="min-h-screen bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/80 backdrop-blur dark:border-neutral-800 dark:bg-neutral-950/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="text-sm font-semibold">
            AppSumo Outcomes
          </Link>

          <nav className="hidden items-center gap-5 text-sm md:flex">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `hover:text-emerald-600 dark:hover:text-emerald-400 ${
                    isActive
                      ? "font-medium text-emerald-600 dark:text-emerald-400"
                      : "text-neutral-700 dark:text-neutral-300"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <Link
                to="/logout"
                className="hidden text-xs text-neutral-500 hover:underline md:inline"
              >
                Log out
              </Link>
            ) : (
              <Link
                to="/login"
                className="hidden rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 md:inline-block"
              >
                Log in
              </Link>
            )}

            <button
              type="button"
              onClick={() => setDrawerOpen((v) => !v)}
              aria-label="Menu"
              aria-expanded={drawerOpen}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 md:hidden dark:border-neutral-800"
            >
              <span aria-hidden className="text-lg">
                {drawerOpen ? "×" : "☰"}
              </span>
            </button>
          </div>
        </div>
      </header>

      {drawerOpen ? (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setDrawerOpen(false)}
        >
          <div className="absolute inset-0 bg-neutral-900/40" />
          <div
            className="absolute inset-x-0 top-14 rounded-b-2xl border-t border-neutral-200 bg-white p-4 shadow-lg dark:border-neutral-800 dark:bg-neutral-950"
            onClick={(e) => e.stopPropagation()}
          >
            <ul className="space-y-1 text-sm">
              {visibleNav.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `block rounded-md px-3 py-2 ${
                        isActive
                          ? "bg-emerald-50 font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                          : "hover:bg-neutral-100 dark:hover:bg-neutral-900"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2">
                {user ? (
                  <Link
                    to="/logout"
                    className="block rounded-md px-3 py-2 text-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    Log out
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="block rounded-md bg-emerald-500 px-3 py-2 text-center text-sm font-medium text-white"
                  >
                    Log in
                  </Link>
                )}
              </li>
            </ul>
          </div>
        </div>
      ) : null}

      <main>{children}</main>
    </div>
  );
}
